// @ts-check
import { advance, createState, groundPound, jump } from './player.js';
import { createLevel, advanceLevel, restartLevel } from './world.js';
import { firePulse } from './projectiles.js';
import { createEvents } from './events.js';
import { createTransfer, UPLINK } from './uplink.js';
import { validateLevel } from './level-state.js';
import { freshInventory, readProgress } from './progress.js';
import { createRun, tickRun, credit, finishRun, targetTime, availableBits, timeLimit } from './scoring.js';
import { createRace, createBest, bankSplit, totals, mastery } from './race.js';
import { playerBody } from './power.js';
import { secretAt } from './secrets.js';
import { availableMedals } from './stage-goals.js';

/** Browser-independent owner of one play session. Content and input are injected.
 * @param {{definition:import('./types.js').LevelDefinition,secretLevels?:readonly import('./types.js').LevelDefinition[],nextLevels?:readonly import('./types.js').LevelDefinition[],mode?:import('./types.js').GameMode,progress?:unknown,now?:()=>number,assist?:boolean}} options
 */
export function createSession({ definition, nextLevels = [], secretLevels = [], mode = 'level', progress, now = () => 0, assist = false }) {
  const routes = [definition, ...nextLevels];
  routes.forEach(validateLevel);
  secretLevels.forEach(validateLevel);
  const saved = readProgress(progress, routes, secretLevels);
  const transistors = { ...saved?.transistors };
  const completed = new Set(saved?.completed ?? []);
  let routeIndex = saved ? routes.findIndex(route => route.id === saved.current) : 0;
  const level = createLevel(routes[routeIndex]);
  level.transistorsFound = transistors[level.definition.id] ?? 0;
  const player = createState(level.spawn);
  const events = createEvents();
  const freeBounds = { width: 1000, height: 300, endless: true };
  const previous = { x: player.x, y: player.y };
  /** @type {import('./uplink.js').Transfer|null} */ let transfer = null;
  let entry = saved?.inventory ?? freshInventory();
  let checkpoint = saved?.checkpoint ?? null;
  let run = saved?.arcade?.run ?? createRun(!checkpoint);
  const records = saved?.arcade?.records ?? {};
  let race = saved?.arcade?.race ?? createRace();
  const best = saved?.arcade?.best ?? createBest();
  if (saved && (!saved.arcade?.race || race.started || run.elapsed > 0 || checkpoint)) race.resumed = true;
  if (saved && !run.finished && (run.elapsed > 0 || checkpoint)) run.eligible = false;
  let realAnchor = now();
  function syncRealClock() {
    const current = now();
    if (race.started && !race.finished) race.realTime = Math.min(32 * 359999, race.realTime + Math.max(0, current - realAnchor) / 1000);
    realAnchor = current;
  }
  /** @type {(import('./scoring.js').Result & {name:string})|null} */ let result = null;
  /** @type {{finale?:boolean,portalId:string,score:number,elapsed:number,level:import('./types.js').Level,player:import('./player.js').Position,entry:typeof entry,checkpoint:typeof checkpoint,run:typeof run,result:(import('./scoring.js').Result & {name:string})|null,clock:number,warned:boolean,snapshot:import('./progress.js').Progress}|null} */
  let excursion = null;
  /** @type {import('./types.js').LevelDefinition|null} */ let destination = null;
  let returning = false;
  let chargingPortal = '';
  let finaleVisited = false;
  let clock = 0;
  let jumpBuffer = 0, warned = false;
  function syncClock() { tickRun(run, Math.max(0, level.time - clock)); clock = level.time; }
  /** Reboot the current route; rewind after completing the complete sequence. @param {boolean} [rewind] */
  function reset(rewind = false) {
    syncRealClock();
    if (transfer?.phase === 'departing' && !rewind) {
      race.splits = race.splits.filter(s => s.id !== level.definition.id);
      if (race.finished) race.practice = true;
      race.finished = false; run.score = Math.max(0, run.score - (result?.bonus ?? 0));
      run.finished = false; run.retries++; result = null;
    }
    if (mode === 'level' && level.started && level.status !== 'won') { run.retries++; run.chain = run.chainTime = 0; }
    if (excursion && destination && level.definition === excursion.level.definition) excursion = null;
    transfer = null; destination = null; returning = false;
    if (rewind) excursion = null;
    jumpBuffer = 0; chargingPortal = '';
    if (rewind) { race = createRace(); mode = 'level'; routeIndex = 0; entry = freshInventory(); checkpoint = null; run = createRun(); result = null; }
    if (!excursion) level.definition = routes[routeIndex];
    restartLevel(level, player);
    level.transistorsFound = transistors[excursion?.level.definition.id ?? level.definition.id] ?? 0;
    clock = 0; warned = false;
    level.bits = entry.bits;
    Object.assign(player, { power: entry.power, size: entry.size, overclock: entry.overclock });
    level.checkpoint = checkpoint;
    if (checkpoint) player.x = level.definition.checkpoints?.find(c => c.id === checkpoint)?.x ?? level.spawn;
    if (mode === 'free') player.x = freeBounds.width / 2;
    Object.assign(previous, { x: player.x, y: player.y });
    events.emit({ type: 'restarted', x: player.x, y: player.y });
  }
  /** @param {import('./types.js').GameEvent} event */
  function accept(event) {
    syncClock(); syncRealClock();
    const points = credit(run, event);
    if (points) events.emit({ type: 'scored', x: event.x, y: event.y, amount: points, chain: run.chain });
    if (event.type === 'completed' && excursion) {
      destination = secretLevels.find(d => d.id === level.definition.secretNext) ?? null;
      returning = !destination;
      transfer = level.definition.quantumFinale ? { ...createTransfer(player, level.finish, true), spectacle: true } : { quantum: true, phase: 'departing', age: 0, x: player.x, y: player.y, gate: player.x };
      player.vx = player.vy = 0;
      events.emit({ ...event, type: 'uplink-entered' });
      return;
    }
    if (event.type === 'completed') {
      result = { ...finishRun(run, level.definition, records, level.time), name: level.definition.name };
      bankSplit(race, best, { id: level.definition.id, score: result.score, time: result.time }, routes);
      completed.add(level.definition.id);
      transfer = { ...createTransfer(player, level.finish, !!level.definition.bandwidthGoal), spectacle: !!level.definition.bandwidthGoal };
      player.vx = player.vy = 0; player.facing = 1;
      player.crouching = player.sliding = false; player.boost = player.impact = 0; player.pound = 'none';
      events.emit({ ...event, type: 'uplink-entered' });
    } else events.emit(event);
  }
  /** @param {number} dt */
  function advanceTransfer(dt) {
    if (!transfer) return;
    transfer.age += Number.isFinite(dt) ? Math.min(0.05, Math.max(0, dt)) : 0;
    if (transfer.phase === 'arriving') {
      if (transfer.age >= UPLINK.arrival) { transfer = null; events.emit({ type: 'arrived', x: player.x, y: player.y }); }
    } else if (transfer.age >= UPLINK.departure + (transfer.approach ?? UPLINK.approach) - UPLINK.approach) {
      if (excursion && (destination || returning)) {
        const inventory = { bits: level.bits, power: player.power, size: player.size, overclock: player.overclock };
        if (level.definition !== excursion.level.definition) { excursion.score += run.score; excursion.elapsed += run.elapsed; }
        if (returning) {
          const home = excursion;
          Object.assign(level, home.level); Object.assign(player, home.player);
          entry = home.entry; checkpoint = home.checkpoint; run = home.run; result = home.result;
          const portal = level.definition.portals?.find(p => p.id === home.portalId);
          const landing = level.definition.checkpoints?.find(c => c.id === portal?.returnCheckpoint);
          if (landing) {
            Object.assign(player, { x: landing.x, y: 0, grounded: true, facing: 1, jumps: 0 });
            checkpoint = landing.id; level.checkpoint = landing.id; entry = inventory;
          }
          clock = home.clock; warned = home.warned; excursion = null; returning = false;
          run.elapsed += home.elapsed;
          level.time += home.elapsed; clock = level.time;
          accept({ type: 'secret-returned', x: player.x, y: player.y, entityId: home.portalId, amount: home.score });
        } else if (destination) {
          level.definition = destination; restartLevel(level, player);
          level.transistorsFound = transistors[excursion?.level.definition.id ?? level.definition.id] ?? 0;
          entry = inventory; checkpoint = null; run = createRun(false); result = null; clock = 0; warned = false;
        }
        level.bits = inventory.bits; Object.assign(player, { power: inventory.power, size: inventory.size, overclock: inventory.overclock, vx: 0, vy: 0 });
        destination = null; jumpBuffer = 0;
        Object.assign(previous, { x: player.x, y: player.y });
        events.emit({ type: 'restarted', x: player.x, y: player.y });
        transfer = { quantum: true, phase: 'arriving', age: 0, x: player.x, y: player.y, gate: player.x };
        events.emit({ type: 'level-entered', x: player.x, y: player.y });
        return;
      }
      if (routeIndex + 1 < routes.length) {
        entry = { bits: level.bits, power: player.power, size: player.size, overclock: player.overclock };
        routeIndex++; checkpoint = null; run = createRun(); transfer = null; reset();
        transfer = { phase: 'arriving', age: 0, x: player.x, y: player.y, gate: player.x };
        events.emit({ type: 'level-entered', x: player.x, y: player.y });
      } else { transfer = null; events.emit({ type: 'completed', x: player.x, y: player.y }); }
    }
  }
  return {
    player, level, previous, events,
    /** Assist: falls return QP to solid ground, and a hit on base QP knocks it back. Either costs an upgrade and the Untouched medal. */
    get assist() { return assist; },
    set assist(value) { assist = !!value; level.assist = assist; },
    /** @param {string} id */
    transistorsFor(id) { return transistors[id] ?? 0; },
    get inSecret() { return excursion !== null; },
    get mainLevel() { return excursion && !excursion.finale ? excursion.level : level; },
    get inQuantumWorld() { return !!excursion?.finale || !!level.definition.quantumFinale; },
    get mainProgress() { return (excursion && !excursion.finale ? excursion.player.x : player.x) / this.mainLevel.finish; },
    get transfer() { return transfer; },
    get levelNumber() { return routeIndex + 1; },
    get routes() { return routes; },
    get run() { return run; },
    get records() { return records; },
    get race() { syncRealClock(); return race; },
    get best() { return best; },
    get totals() { return excursion && level.definition !== excursion.level.definition ? totals(race, { ...excursion.run, score: excursion.run.score + excursion.score + run.score, elapsed: excursion.run.elapsed + excursion.elapsed + run.elapsed }, excursion.level.definition.id) : totals(race, run, level.definition.id); },
    /** @param {string} id */
    medalsFor(id) { const d = routes.find(d => d.id === id); return d ? availableMedals(d) : []; },
    get mastery() { return mastery(records, routes); },
    get remaining() { const left = timeLimit(this.mainLevel.definition) - this.mainLevel.time - (excursion && !excursion.finale && level.definition !== excursion.level.definition ? level.time : 0); return left < 1e-7 ? 0 : left; },
    get result() { return result; },
    get targets() { return { time: targetTime(level.definition), limit: timeLimit(level.definition), bits: availableBits(level.definition) }; },
    get completed() { return [...completed]; },
    get quantumUnlocked() { return routes.length === 32 && completed.has('8-4') && routes.every(d => transistors[d.id] === 7); },
    /** The collection unlock persists through the existing completed stages and three-bit masks. */
    enterQuantumWorld() {
      const first = secretLevels.find(d => d.id === 'quantum-1' && d.quantumFinale);
      if (!this.quantumUnlocked || !first || excursion) return false;
      const home = this.snapshot();
      excursion = { finale: true, portalId: 'quantum-world', score: 0, elapsed: 0, level: { ...level }, player: { ...player, vx: 0, vy: 0 }, entry, checkpoint, run, result, clock, warned, snapshot: home };
      destination = first; finaleVisited = true; race.practice = true;
      transfer ??= { quantum: true, phase: 'departing', age: 0, x: player.x, y: player.y, gate: player.x };
      events.emit({ type: 'secret-entered', x: player.x, y: player.y, reason: 'Quantum world unlocked' });
      return true;
    },
    /** @param {string} id */
    unlocked(id) { const index = routes.findIndex(route => route.id === id); return index >= 0 && routes.slice(0, index).every(route => completed.has(route.id)); },
    /** Select an unlocked route without discarding completed stages. @param {string} id */
    select(id) {
      const index = routes.findIndex(route => route.id === id);
      if (index < 0 || !routes.slice(0, index).every(route => completed.has(route.id))) return false;
      excursion = null; destination = null; returning = false; transfer = null; race = createRace(true); routeIndex = index; checkpoint = null; entry = freshInventory(); mode = 'level'; reset(); run = createRun(); result = null; return true;
    },
    /** @returns {import('./progress.js').Progress} */
    snapshot() {
      syncRealClock();
      if (excursion) return { ...structuredClone(excursion.snapshot), transistors: { ...transistors } };
      const advancing = transfer?.phase === 'departing' && routeIndex + 1 < routes.length;
      return { layout: 1, transistors: { ...transistors }, version: 1, current: routes[routeIndex + Number(advancing)].id, completed: [...completed], checkpoint: advancing ? null : checkpoint,
        complete: routeIndex === routes.length - 1 && level.status === 'won',
        arcade: { race: { ...race, splits: race.splits.map(s => ({ ...s })) }, best: { ...best, splits: best.splits.map(s => ({ ...s })) }, run: advancing ? createRun() : { ...run, credited: [...run.credited] }, records: Object.fromEntries(Object.entries(records).map(([id, r]) => [id, { ...r, medals: [...r.medals] }])) },
        inventory: advancing ? { bits: level.bits, power: player.power, size: player.size, overclock: player.overclock } : { ...entry } };
    },
    get nextLevel() { return destination ?? (excursion ? returning ? excursion.level.definition : secretLevels.find(d => d.id === level.definition.secretNext) ?? excursion.level.definition : routes[routeIndex + 1] ?? null); },
    get mode() { return mode; },
    /** @param {import('./types.js').GameMode} next */
    setMode(next) { if (next !== mode && race.started) { race.practice = true; run.eligible = false; } if (excursion) { Object.assign(level, excursion.level); entry = excursion.entry; checkpoint = excursion.checkpoint; run = excursion.run; excursion = null; } mode = next; reset(); },
    reset,
    /** Free-roam bounds only; level simulation never depends on screen geometry.
     * @param {number} width @param {number} height
     */
    resizeFree(width, height) {
      freeBounds.width = Math.max(0, width); freeBounds.height = Math.max(0, height);
    },
    /** @param {import('./types.js').Action} action */
    command(action) {
      if (transfer) return;
      if (mode === 'level' && level.status !== 'playing') return;
      if (action === 'pulse' && mode === 'level') firePulse(level, player, accept);
      if (action === 'jump') {
        if (jump(player)) { jumpBuffer = 0; events.emit({ type: player.jumps === 2 ? 'double-jump' : 'jump', x: player.x, y: player.y }); }
        else if (player.pound === 'none' && !player.recoil) jumpBuffer = .12;
      }
      if (action === 'down' && groundPound(player)) events.emit({ type: 'ground-pound', x: player.x, y: player.y });
    },
    /** @param {{direction:number,sprint:boolean,down:boolean}} input @param {number} seconds */
    step(input, seconds) {
      syncRealClock();
      // Every rebuilt level instance inherits the player's Assist choice.
      level.assist = assist;
      if (transfer) { advanceTransfer(seconds); return; }
      if (mode === 'level' && level.status === 'playing' && (level.started || input.direction || !player.grounded || input.down)) race.started = true;
      Object.assign(previous, { x: player.x, y: player.y });
      if (mode === 'level' && !excursion && level.status === 'playing') {
        const portal = secretAt(level, player, input.down);
        const target = portal && secretLevels.find(d => d.id === portal.target);
        const charging = portal && target && (portal.kind !== 'pipe' || input.direction === 0 && Math.abs(player.vx) < 12);
        if (!charging || chargingPortal !== portal.id) level.secretCharge = 0;
        chargingPortal = charging ? portal.id : ''; level.secretEntrance = chargingPortal;
        if (charging) level.secretCharge = Math.min(.65, level.secretCharge + Math.max(0, Math.min(.05, seconds)));
        if (portal && target && (portal.kind !== 'pipe' || level.secretCharge >= .65 - 1e-8)) {
          level.secretCharge = 0; chargingPortal = '';
          run.eligible = false; race.practice = true;
          const savedHome = this.snapshot();
          level.signals[`visited:${portal.id}`] = true;
          excursion = { portalId: portal.id, score: 0, elapsed: 0, level: { ...level }, player: { ...player, vx: 0, vy: 0 }, entry, checkpoint, run, result, clock, warned, snapshot: savedHome };
          destination = target;
          transfer = { quantum: true, spectacle: true, phase: 'departing', age: 0, x: player.x, y: player.y, gate: player.x + 11 };
          player.vx = player.vy = 0; jumpBuffer = 0;
          events.emit({ type: 'secret-entered', x: player.x, y: player.y, reason: target.name });
          events.emit({ type: 'uplink-entered', x: player.x, y: player.y });
          return;
        }
      }
      if (mode === 'level' && level.status === 'playing') {
        const body = playerBody(player);
        for (const [index, item] of (level.definition.transistors ?? []).entries()) {
          const slot = item.slot ?? index;
          const bit = 1 << slot;
          if (!(level.transistorsFound & bit) && player.x + body.width > item.x - 12 && player.x < item.x + 12 && player.y + body.height > item.y - 14 && player.y < item.y + 14) {
            level.transistorsFound |= bit;
            transistors[this.mainLevel.definition.id] = level.transistorsFound;
            this.mainLevel.transistorsFound = level.transistorsFound;
            events.emit({ type: 'transistor-found', x: item.x, y: item.y, entityId: String(slot), amount: slot + 1 });
          }
        }
      }
      const wasAirborne = !player.grounded;
      if (mode === 'level') {
        advanceLevel(level, player, input.direction, Math.min(seconds, Math.max(0, this.remaining)), input, accept); syncClock();
        if (level.definition.id === '8-4' && level.status === 'won' && !excursion && !finaleVisited) this.enterQuantumWorld();
        if (!warned && level.status === 'playing' && this.remaining <= 30) {
          warned = true; events.emit({ type: 'time-warning', x: player.x, y: player.y });
        }
        if (level.status === 'playing' && level.started && this.remaining <= 1e-7) {
          level.status = 'dead'; level.reason = 'Time expired'; player.vx = player.vy = 0;
          accept({ type: 'failed', x: player.x, y: player.y, reason: level.reason });
        }
      }
      else advance(player, input.direction, seconds, freeBounds, input);
      if (wasAirborne && player.grounded) events.emit({ type: 'land', x: player.x, y: player.y });
      if (jumpBuffer > 0) {
        jumpBuffer = Math.max(0, jumpBuffer - Math.max(0, Math.min(.05, seconds)));
        if (player.grounded && (mode === 'free' || level.status === 'playing') && jump(player)) { jumpBuffer = 0; events.emit({ type: 'jump', x: player.x, y: player.y }); }
      }
      if (mode === 'level' && level.status === 'playing' && player.grounded && player.y === 0) {
        const reached = [...(level.definition.checkpoints ?? [])].reverse().find(c => player.x >= c.x);
        const previousCheckpoint = level.definition.checkpoints?.find(c => c.id === checkpoint);
        if (reached && reached.x > (previousCheckpoint?.x ?? -1)) {
          checkpoint = reached.id; level.checkpoint = checkpoint;
          entry = { bits: level.bits, power: player.power, size: player.size, overclock: player.overclock };
          accept({ type: 'checkpoint', x: reached.x, y: 0, entityId: reached.id, reason: reached.name });
        }
      }
    },
    pause() {
      syncRealClock();
      jumpBuffer = 0; level.secretCharge = 0; chargingPortal = '';
      player.vx = 0; player.crouching = player.sliding = false; player.boost = player.impact = 0;
      Object.assign(previous, { x: player.x, y: player.y });
    },
    get active() {
      if (transfer) return true;
      return mode === 'level' ? level.started && level.status === 'playing'
        : !!(player.vx || player.vy || !player.grounded || player.impact || player.pound !== 'none');
    },
    dispose() { events.clear(); },
  };
}
/** @typedef {ReturnType<typeof createSession>} Session */
